import React from 'react';
import { Calendar } from 'lucide-react';
import { Button } from '@/components/base';

export interface DateRangePickerProps {
  startDate: Date;
  endDate: Date;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
}

/**
 * Date range picker with quick presets
 */
export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  const formatDateForInput = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  const handlePreset = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    onStartDateChange(start);
    onEndDateChange(end);
  };

  const handleThisMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    // Cap end date at today (backend rejects future dates)
    const end = now;
    onStartDateChange(start);
    onEndDateChange(end);
  };

  const handleLastMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    onStartDateChange(start);
    onEndDateChange(end);
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      {/* Date inputs */}
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-gray-600" />
        <input
          type="date"
          value={formatDateForInput(startDate)}
          onChange={(e) => onStartDateChange(new Date(e.target.value))}
          max={formatDateForInput(endDate)}
          className="rounded-lg border border-gray-300 px-3 py-1.5 font-hanken text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <span className="text-gray-500">to</span>
        <input
          type="date"
          value={formatDateForInput(endDate)}
          onChange={(e) => onEndDateChange(new Date(e.target.value))}
          min={formatDateForInput(startDate)}
          max={formatDateForInput(new Date())}
          className="rounded-lg border border-gray-300 px-3 py-1.5 font-hanken text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      {/* Quick presets */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handlePreset(7)}
          className="h-auto rounded-full bg-gray-100 px-3 py-1 font-hanken text-xs font-medium text-gray-700 hover:bg-gray-200"
        >
          Last 7 days
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handlePreset(30)}
          className="h-auto rounded-full bg-gray-100 px-3 py-1 font-hanken text-xs font-medium text-gray-700 hover:bg-gray-200"
        >
          Last 30 days
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handlePreset(90)}
          className="h-auto rounded-full bg-gray-100 px-3 py-1 font-hanken text-xs font-medium text-gray-700 hover:bg-gray-200"
        >
          Last 90 days
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleThisMonth}
          className="h-auto rounded-full bg-gray-100 px-3 py-1 font-hanken text-xs font-medium text-gray-700 hover:bg-gray-200"
        >
          This month
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLastMonth}
          className="h-auto rounded-full bg-gray-100 px-3 py-1 font-hanken text-xs font-medium text-gray-700 hover:bg-gray-200"
        >
          Last month
        </Button>
      </div>
    </div>
  );
};
